<template>
  <content-modal
    ref="dynamicInternalLinkModalRef"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    class="dynamic-link-modal"
    @modal-hide-request="handleHideRequest"
    @modal-hidden="close"
  >
    <template #title>{{ t("dynamic_internal_link_add_edit_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <div class="modal-content">
            <inline-loader v-if="isLoading" />
            <dynamic-internal-link-settings
              v-else
              ref="settingsRef"
              :link-object="linkObject"
              @change="handleSettingsUpdate"
            />
          </div>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="handleExitAction">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="saveBtn.isSaving"
            :error="saveBtn.isError"
            :error-message="saveBtn.errorMessage"
            :tip-position="saveBtn.dataSaveBtnTipPosition"
            @click="handleSave"
          >
            <span v-if="saveBtn.isError">{{ t("button_state_error") }}</span>
            <span v-else>{{ saveBtn.btnText }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import modalEventBus from "@plugins/modal/service/event-bus.ts";
import { useDynamicLinkStore } from "@plugins/dynamic-link/dynamic.link.store.ts";
import {
  defineAsyncComponent,
  getCurrentInstance,
  nextTick,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
} from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";

const DynamicInternalLinkSettings = defineAsyncComponent(
  () => import("@plugins/dynamic-link/components/DynamicInternalLinkSettings.vue"),
);

defineOptions({
  name: "DynamicInternalLinkModal",
});

const props = defineProps({
  params: {
    type: Object,
    required: true,
    validator(params: object) {
      return "linkObject" in params && "lessonUuid" in params;
    },
  },
});

const emit = defineEmits(["save"]);
const { t } = useI18n();
const store = useDynamicLinkStore();
const errorHandler = useErrorHandler();

const visible = ref(true);
const isLoading = ref(true);
const closeAriaLabel = "Close";
const size = "medium";
const autoHideOff = true;
const isDirty = ref(false);
const settingsRef = useTemplateRef("settingsRef");
const proxy = getCurrentInstance()?.proxy;
if (!proxy) throw new Error("Proxy is not available");

const saveBtn = reactive({
  isSaving: false,
  isError: false,
  errorMessage: "",
  dataSaveBtnTipPosition: "top",
  btnText: t("button_save"),
});

const linkObject = reactive({
  anchor: props.params.linkObject.anchor,
  dataLinkType: props.params.linkObject.dataLinkType,
  customText: props.params.linkObject.customText,
});

function resetProcessingState() {
  saveBtn.isSaving = false;
  saveBtn.isError = false;
}

function promptPageDirty() {
  if (confirm(t("dirty_confirmation_prompt"))) {
    close();
  } else {
    resetProcessingState();
  }
}

function close() {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", proxy.$.vnode.key);
}

function handleExitAction() {
  if (!isDirty.value) {
    close();
    return;
  }
  promptPageDirty();
}

function handleHideRequest($event: { "cv:reason": string }) {
  // modal-hide-request no longer emits a PointerEvent, instead just the reason is emitted.
  if ($event["cv:reason"] !== "external-click") {
    handleExitAction();
  }
}

function isNewValue(key: string, value: string): boolean {
  return value !== props.params.linkObject[key];
}

function handleSettingsUpdate(obj: Record<string, string>): void {
  for (const [key, value] of Object.entries(obj)) {
    if (key in linkObject) {
      linkObject[key] = value;
      isDirty.value = isNewValue(key, value);
      saveBtn.isError = false;
    }
  }
}

function validate(): boolean {
  if (settingsRef.value) {
    return settingsRef.value.validate();
  }
  return false;
}

function handleSave() {
  if (validate()) {
    saveBtn.isSaving = true;
    emit("save", { ...linkObject });
    isDirty.value = false;
    nextTick(() => {
      saveBtn.isSaving = false;
      close();
    });
  } else {
    saveBtn.isError = true;
  }
}

onMounted(() => {
  store
    .fetchLessonAnchor(props.params.lessonUuid)
    .then(() => (isLoading.value = false))
    .catch(errorHandler.passiveApiFailHandler);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.dynamic-link-modal {
  .bx--modal-container {
    max-width: 480px;
  }
  .bx--modal-content {
    overflow-y: visible;
    .field-set {
      .bx--dropdown {
        max-height: 40px;
        // placeholder
        span {
          @include carbon--type-style("body-short-01", true);
          color: $text-03;
        }
        // selected
        .dropdown-option {
          > span {
            @include carbon--type-style("productive-heading-01", true);
            color: $text-02;
          }
        }
        ul {
          li {
            > .bx--dropdown-link {
              .dropdown-option {
                @include carbon--type-style("productive-heading-01");
                color: $text-02;
                > .sls-icon {
                  min-width: 32px;
                  margin-right: $spacing-02;
                }
              }
              .activity-dropdown-option {
                padding-left: $spacing-06;
              }
            }
          }
        }
      }
    }
  }
}
</style>
